import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  Checkbox,
  TableContainer,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import BoothChip from '../common/BoothChip';
import BoothSelectionMap from '../vendor/BoothSelectionMap';
import CategoryChip from '../common/CategoryChip';
import { getVendorContactEmail, getVendorIdentityLines } from '../../utils/vendorContact';

const PHOTO_ORIGIN = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
const resolvePhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.url?.startsWith('http')) return photo.url;
  return `${PHOTO_ORIGIN}${photo.url || ''}`;
};

const DENIAL_REASONS = [
  'Booth no longer available',
  'Incomplete information',
  'Did not meet criteria',
  'Duplicate registration',
  'Other',
];

const RELEASE_REASONS = [
  'Vendor unresponsive',
  'Booth needed for another vendor',
  'Vendor requested release',
  'Other',
];

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
};

const formatRelative = (s) => {
  if (!s) return null;
  const then = new Date(s).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const formatCountdown = (seconds) => {
  if (typeof seconds !== 'number') return null;
  if (seconds <= 0) return 'expired';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h left`;
  const days = Math.round(hours / 24);
  return `${days}d left`;
};

// Shared table used by both the Home page (compact, first N rows) and the
// full Approvals page. All approve/deny calls funnel through this so we
// never grow another parallel implementation again.
const ApprovalsQueueTable = ({
  rows = [],
  allRows,
  loading = false,
  onChange,
  status = 'pending', // 'pending' | 'incomplete' | 'denied'
  showSelection = false,
  selected = [],
  onSelectionChange,
  maxRows,
  emptyText,
  hideHeader = false,
}) => {
  const navigate = useNavigate();
  const [approveDialog, setApproveDialog] = useState(null);
  // 'single' approves only the clicked booth; 'all' approves every pending
  // booth for the vendor. Determined by the choice dialog when the vendor
  // has more than one pending registration.
  const [approveScope, setApproveScope] = useState('all');
  const [approveMarkAsPaid, setApproveMarkAsPaid] = useState(false);
  const [approveChoice, setApproveChoice] = useState(null);
  const [denyDialog, setDenyDialog] = useState(null);
  const [releaseDialog, setReleaseDialog] = useState(null);
  const [reopenDialog, setReopenDialog] = useState(null);
  const [reopenNotes, setReopenNotes] = useState('');
  const [photoDialog, setPhotoDialog] = useState(null);
  const [nudgeLoading, setNudgeLoading] = useState({});
  const [promoteLoading, setPromoteLoading] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState(DENIAL_REASONS[0]);
  const [releaseReason, setReleaseReason] = useState(RELEASE_REASONS[0]);
  const [releaseDetail, setReleaseDetail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSendError, setEmailSendError] = useState('');
  const [changeBoothDialog, setChangeBoothDialog] = useState(null);

  const visible = typeof maxRows === 'number' ? rows.slice(0, maxRows) : rows;
  const queueRows = allRows ?? rows;

  // Count how many pending booth registrations a vendor currently has in
  // the queue. Used to decide whether to ask the admin to scope
  // the approval to a single booth or all of them.
  const pendingRegistrationsForVendor = (vendorId) =>
    queueRows.filter(
      (r) =>
        r.vendor_id === vendorId &&
        r.registration_id &&
        (!r.booth_registration_status ||
          ['soft_held', 'pending_approval'].includes(r.booth_registration_status))
    );

  const openApproveFlow = (row, markAsPaid = false) => {
    const pending = pendingRegistrationsForVendor(row.vendor_id);
    if (pending.length > 1 && row.registration_id) {
      setApproveChoice({ row, count: pending.length, markAsPaid });
    } else {
      setApproveScope('all');
      setApproveMarkAsPaid(markAsPaid);
      setApproveDialog(row);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const body = { notes };
      if (approveMarkAsPaid) {
        body.markAsPaid = true;
      }
      if (approveScope === 'single' && approveDialog.registration_id) {
        body.registrationId = approveDialog.registration_id;
      }
      const response = await api.post(`/admin/vendors/${approveDialog.vendor_id}/approve`, body);
      setApproveDialog(null);
      setApproveScope('all');
      setApproveMarkAsPaid(false);
      setNotes('');
      if (response.data?.markAsPaid && response.data?.emailsSent === 0) {
        setSuccessMessage(
          'Vendor approved and marked paid. No confirmation email was sent (no contact email on file).'
        );
      }
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!denyDialog) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await api.post(`/admin/vendors/${denyDialog.vendor_id}/deny`, {
        denialReason: reason,
        notes,
        boothIds: denyDialog.booth_id ? [denyDialog.booth_id] : [],
        registrationId: denyDialog.registration_id,
      });
      setDenyDialog(null);
      setNotes('');
      setReason(DENIAL_REASONS[0]);
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deny');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromote = async (row) => {
    if (!row.registration_id) return;
    setPromoteLoading((p) => ({ ...p, [row.registration_id]: true }));
    try {
      await api.post(`/admin/booth-registrations/${row.registration_id}/status`, {
        status: 'pending_approval',
      });
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote registration');
    } finally {
      setPromoteLoading((p) => ({ ...p, [row.registration_id]: false }));
    }
  };

  // Map the table's tab into the email copy the backend should use. The
  // backend validates this so we keep the set narrow.
  const stageForRow = (row) => {
    if (status === 'pending') return 'pending_approval';
    if (status === 'incomplete') return row.booth_id ? 'pending_approval' : 'profile';
    return 'pending_approval';
  };

  const openEmailDialog = (row) => {
    setEmailDialog(row);
    setEmailSubject('');
    setEmailMessage('');
    setEmailSendError('');
  };

  const handleSendVendorEmail = async () => {
    if (!emailDialog) return;
    setEmailSending(true);
    setEmailSendError('');
    try {
      await api.post(`/admin/vendors/${emailDialog.vendor_id}/email`, {
        subject: emailSubject,
        message: emailMessage,
      });
      setEmailDialog(null);
      setEmailSubject('');
      setEmailMessage('');
      setSuccessMessage('Email sent successfully.');
    } catch (err) {
      setEmailSendError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleNudge = async (row) => {
    setNudgeLoading((p) => ({ ...p, [row.vendor_id]: true }));
    setError('');
    try {
      await api.post(`/admin/vendors/${row.vendor_id}/nudge`, {
        stage: stageForRow(row),
      });
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reminder email');
    } finally {
      setNudgeLoading((p) => ({ ...p, [row.vendor_id]: false }));
    }
  };

  const handleReopen = async () => {
    if (!reopenDialog) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await api.post(`/admin/vendors/${reopenDialog.vendor_id}/reopen`, {
        notes: reopenNotes,
      });
      setReopenDialog(null);
      setReopenNotes('');
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen vendor');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeBooth = async (boothData) => {
    if (!changeBoothDialog?.registration_id) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await api.patch(
        `/admin/booth-registrations/${changeBoothDialog.registration_id}/booth`,
        { boothId: boothData.id }
      );
      setChangeBoothDialog(null);
      setSuccessMessage(`Booth changed to ${boothData.cell_name}.`);
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change booth');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!releaseDialog) return;
    setActionLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const composedReason = releaseDetail
        ? `${releaseReason} — ${releaseDetail}`
        : releaseReason;
      await api.post(`/admin/booth-registrations/${releaseDialog.registration_id}/release`, {
        reason: composedReason,
      });
      setReleaseDialog(null);
      setReleaseDetail('');
      setReleaseReason(RELEASE_REASONS[0]);
      if (onChange) await onChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to release booth');
    } finally {
      setActionLoading(false);
    }
  };

  const allSelected = visible.length > 0 && visible.every((r) => selected.includes(r.vendor_id));
  const someSelected = visible.some((r) => selected.includes(r.vendor_id)) && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(selected.filter((id) => !visible.some((r) => r.vendor_id === id)));
    } else {
      const nextIds = visible.map((r) => r.vendor_id);
      const merged = Array.from(new Set([...selected, ...nextIds]));
      onSelectionChange(merged);
    }
  };

  const toggleOne = (vendorId) => {
    if (!onSelectionChange) return;
    if (selected.includes(vendorId)) {
      onSelectionChange(selected.filter((id) => id !== vendorId));
    } else {
      onSelectionChange([...selected, vendorId]);
    }
  };

  return (
    <Box>
      {error ? (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      ) : null}
      {successMessage ? (
        <Typography color="success.main" sx={{ mb: 1 }}>
          {successMessage}
        </Typography>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          {!hideHeader && (
            <TableHead>
              <TableRow>
                {showSelection && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                )}
                <TableCell>Vendor</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Booth</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={showSelection ? 6 : 5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showSelection ? 6 : 5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyText || 'No items.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((row) => (
                <TableRow key={`${row.vendor_id}-${row.registration_id || row.booth_id || 'x'}`} hover>
                  {showSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row.vendor_id)}
                        onChange={() => toggleOne(row.vendor_id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {(() => {
                      const { primary, secondary } = getVendorIdentityLines(row);
                      return (
                        <>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {primary}
                            </Typography>
                            {typeof row.product_photo_count === 'number' && row.product_photo_count > 0 ? (
                              <Tooltip title={`Quick view ${row.product_photo_count} product photo${row.product_photo_count === 1 ? '' : 's'}`}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  clickable
                                  onClick={() => setPhotoDialog(row)}
                                  icon={<PhotoLibraryIcon fontSize="small" />}
                                  label={row.product_photo_count}
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>
                          {secondary ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {secondary}
                            </Typography>
                          ) : null}
                        </>
                      );
                    })()}
                    {row.exhibit_type ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {row.exhibit_type}
                      </Typography>
                    ) : null}
                    <CategoryChip row={row} sx={{ mt: 0.5 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{getVendorContactEmail(row) || '—'}</Typography>
                    {row.phone ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.phone}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {row.booth_name ? (
                      <BoothChip
                        name={row.booth_name}
                        isCorner={row.is_corner}
                        needsElectricity={row.needs_electricity}
                      />
                    ) : (
                      <Chip size="small" label="No booth" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2">
                        Submitted {formatDate(row.registration_date || row.created_at)}
                      </Typography>
                      {row.last_activity_at ? (
                        <Typography variant="caption" color="text.secondary">
                          Active {formatRelative(row.last_activity_at)}
                        </Typography>
                      ) : null}
                      {row.booth_registration_status === 'soft_held' && row.held_until ? (
                        <Chip
                          size="small"
                          color={row.seconds_until_expiry < 600 ? 'error' : 'warning'}
                          label={`Soft hold · ${formatCountdown(row.seconds_until_expiry)}`}
                        />
                      ) : null}
                      {row.booth_registration_status === 'expired' ? (
                        <Chip size="small" variant="outlined" label="Hold expired" />
                      ) : null}
                      {!row.booth_id && status === 'incomplete' ? (
                        <Chip size="small" color="error" variant="outlined" label="No booth picked" />
                      ) : null}
                      {row.nudges_sent > 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.nudges_sent} nudge{row.nudges_sent === 1 ? '' : 's'} sent
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
                      <Tooltip title="View vendor details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/vendors/${row.vendor_id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Email Vendor">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openEmailDialog(row)}
                            disabled={!getVendorContactEmail(row)}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {(status === 'pending' || status === 'incomplete') && (
                        <Tooltip title="Send a reminder email to the vendor">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleNudge(row)}
                              disabled={!!nudgeLoading[row.vendor_id]}
                            >
                              {nudgeLoading[row.vendor_id] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <NotificationsActiveIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {(status === 'pending' || status === 'incomplete') && row.registration_id && (
                        <Tooltip title="Release this booth back to the pool">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => setReleaseDialog(row)}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {status === 'pending' && row.booth_id && row.registration_id && (
                        <Tooltip title="Change booth">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setChangeBoothDialog(row)}
                              disabled={actionLoading}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openApproveFlow(row, false)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Approve & Mark Paid">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openApproveFlow(row, true)}
                              >
                                <PaidIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Deny">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDenyDialog(row)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}

                      {status === 'incomplete' && row.booth_id && (
                        <Tooltip title="Move to Pending Approval">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handlePromote(row)}
                              disabled={!!promoteLoading[row.registration_id]}
                            >
                              {promoteLoading[row.registration_id] ? <CircularProgress size={16} /> : 'Send to review'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}

                      {status === 'denied' && (
                        <>
                          {row.denial_reason ? (
                            <Tooltip title={row.denial_reason}>
                              <Chip size="small" color="error" label="Denied" />
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Reopen this vendor for review (no email sent)">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setReopenNotes('');
                                setReopenDialog(row);
                              }}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!changeBoothDialog}
        onClose={() => !actionLoading && setChangeBoothDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Change booth for {changeBoothDialog?.business_name}
          {changeBoothDialog?.booth_name ? ` (currently ${changeBoothDialog.booth_name})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click an available booth on the map. The registration stays in pending approval; electricity and special requests are unchanged.
          </Typography>
          <BoothSelectionMap onSelect={handleChangeBooth} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeBoothDialog(null)} disabled={actionLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!approveDialog}
        onClose={() => {
          setApproveDialog(null);
          setApproveMarkAsPaid(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approveMarkAsPaid ? 'Approve & Mark Paid' : 'Approve'} {approveDialog?.business_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {approveMarkAsPaid
              ? approveScope === 'single' && approveDialog?.booth_name
                ? `Marks booth ${approveDialog.booth_name} as approved and paid, records a manual payment, and sends a confirmation email (not a payment request). Other pending booths stay in the queue.`
                : 'Marks all pending booth registrations as approved and paid, records manual payments, and sends a confirmation email (not a payment request). Skips the 14-day online payment window.'
              : approveScope === 'single' && approveDialog?.booth_name
                ? `This approves only booth ${approveDialog.booth_name} and emails the vendor. Their other pending booths stay in the queue.`
                : 'This approves all pending booth registrations for this vendor for the current festival and emails the vendor.'}
          </Typography>
          {approveMarkAsPaid ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {getVendorContactEmail(approveDialog)
                ? `Confirmation email will be sent to ${getVendorContactEmail(approveDialog)}.`
                : 'No contact email on file — registration will be completed without sending an email.'}
            </Typography>
          ) : null}
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveDialog(null);
              setApproveScope('all');
              setApproveMarkAsPaid(false);
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color={approveMarkAsPaid ? 'primary' : 'success'}
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} />
              ) : approveMarkAsPaid ? (
                <PaidIcon />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {approveMarkAsPaid
              ? approveScope === 'single' && approveDialog?.booth_name
                ? `Approve & mark booth ${approveDialog.booth_name} paid`
                : 'Approve & Mark Paid'
              : approveScope === 'single' && approveDialog?.booth_name
                ? `Approve booth ${approveDialog.booth_name}`
                : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!approveChoice}
        onClose={() => setApproveChoice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Multiple booths pending</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {approveChoice?.row?.business_name || 'This vendor'} has{' '}
            <strong>{approveChoice?.count}</strong> pending booth registrations
            {approveChoice?.row?.booth_name
              ? ` (you clicked booth ${approveChoice.row.booth_name}).`
              : '.'}{' '}
            {approveChoice?.markAsPaid ? 'Approve and mark paid for' : 'Approve'} only the booth you
            clicked, or all of them?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setApproveChoice(null)}>Cancel</Button>
          <Button
            onClick={() => {
              const { row, markAsPaid } = approveChoice;
              setApproveChoice(null);
              setApproveScope('single');
              setApproveMarkAsPaid(!!markAsPaid);
              setApproveDialog(row);
            }}
            variant="outlined"
            color={approveChoice?.markAsPaid ? 'primary' : 'success'}
          >
            Only this booth
          </Button>
          <Button
            onClick={() => {
              const { row, markAsPaid } = approveChoice;
              setApproveChoice(null);
              setApproveScope('all');
              setApproveMarkAsPaid(!!markAsPaid);
              setApproveDialog(row);
            }}
            variant="contained"
            color={approveChoice?.markAsPaid ? 'primary' : 'success'}
          >
            {approveChoice?.markAsPaid ? 'Approve & mark all paid' : `Approve all ${approveChoice?.count}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!denyDialog} onClose={() => setDenyDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Deny {denyDialog?.business_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Denies the selected booth registration. If this is the vendor&apos;s only booth, the whole enrollment is denied and the vendor is emailed.
          </Typography>
          <TextField
            select
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {DENIAL_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Internal notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyDialog(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeny}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            Deny
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!releaseDialog}
        onClose={() => setReleaseDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Release {releaseDialog?.booth_name ? `Booth ${releaseDialog.booth_name}` : 'booth'} for {releaseDialog?.business_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The booth will be marked expired and returned to the available pool. The vendor will receive an email letting them know.
          </Typography>
          <TextField
            select
            label="Reason"
            value={releaseReason}
            onChange={(e) => setReleaseReason(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {RELEASE_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Internal note (optional)"
            value={releaseDetail}
            onChange={(e) => setReleaseDetail(e.target.value)}
            multiline
            rows={2}
            fullWidth
            helperText="Appended to the audit trail. Vendor sees only that the booth was released by an admin."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialog(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRelease}
            variant="contained"
            color="warning"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <LinkOffIcon />}
          >
            Release booth
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!reopenDialog}
        onClose={() => !actionLoading && setReopenDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reopen {reopenDialog?.business_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Moves this vendor (and all of its denied booth registrations) back to pending approval.
            The original denial reason will be preserved in the admin notes. The vendor will <strong>not</strong> be emailed.
          </Typography>
          <TextField
            label="Internal note (optional)"
            value={reopenNotes}
            onChange={(e) => setReopenNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText="Appended to admin notes alongside the reopen timestamp."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialog(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReopen}
            variant="contained"
            color="warning"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <RestartAltIcon />}
          >
            Reopen review
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!photoDialog}
        onClose={() => setPhotoDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Product photos{photoDialog?.business_name ? ` — ${photoDialog.business_name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {Array.isArray(photoDialog?.product_photos) && photoDialog.product_photos.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 1.5,
              }}
            >
              {photoDialog.product_photos.map((photo) => {
                const url = resolvePhotoUrl(photo);
                return (
                  <a
                    key={photo.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.12)',
                    }}
                  >
                    <img
                      src={url}
                      alt={photo.original_name || 'Product photo'}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: 180,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </a>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No product photos submitted for this festival.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!emailDialog}
        onClose={() => !emailSending && setEmailDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Email Vendor</DialogTitle>
        <DialogContent>
          {emailDialog ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                To: {getVendorContactEmail(emailDialog) || '—'}
              </Typography>
              {emailDialog.business_name ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {emailDialog.business_name}
                </Typography>
              ) : null}
              <TextField
                label="Subject"
                fullWidth
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Message"
                fullWidth
                multiline
                minRows={5}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
              {emailSendError ? (
                <Typography color="error" sx={{ mt: 2 }}>
                  {emailSendError}
                </Typography>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(null)} disabled={emailSending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendVendorEmail}
            variant="contained"
            color="primary"
            disabled={emailSending || !emailSubject.trim() || !emailMessage.trim()}
          >
            {emailSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalsQueueTable;
